import { apiGet, APIURL, appendFlatpickrBaseStyles, cancelSelectEvent, changeSymbolForWidget, getCSSStyles, getHeaderPinButtonHTML, getHeaderPinStateClass, getNoDataHTML, getSymbolDropdown, getSymbolOptions, getTraderSMCardHTML, getWidgetDataURI, initFlatpickrInShadow, isHeaderPinned, normalizeWidgetTheme, setupHeaderPin, toggleWidgetTheme, searchSymbols } from '../../helpers';
import { WidgetConfig, WidgetEvent, WidgetEventHandler, WidgetEventType } from '../../types';
import flatpickr from 'flatpickr';

interface ExchangeCardsWidgetConfig extends WidgetConfig {
    selectedExchange?: string;
}

export class TraderverseMarketExchangeCards {
    private container: HTMLElement;
    private config: ExchangeCardsWidgetConfig;
    private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
    private updateInterval: number | null = null;
    private shadowRoot: ShadowRoot;
    private currentData: any = null;
    private currentChart: any;
    private widgetResizeObserver: ResizeObserver | null = null;
    private currentInterval: string = '1w';
    private outsideClickHandler: ((ev: MouseEvent) => void) | null = null;

    private selectedSymbols: string[] = ['AAPL', 'AMZN'];

    private getAllSymbolOptions(): any[] {
        return (window as any).getSymbolOptions ? (window as any).getSymbolOptions() : getSymbolOptions();
    }

    constructor(config: WidgetConfig) {
        this.config = { ...this.getDefaultConfig(), ...config };
        this.config.theme = normalizeWidgetTheme(this.config.theme);
        this.container = this.resolveContainer(config.container);

        if ((this.container as any).shadowRoot) {
            this.shadowRoot = (this.container as any).shadowRoot;
        } else {
            this.shadowRoot = this.container.attachShadow({ mode: 'closed' });
        }
        this.init();

        this.widgetResizeObserver = new ResizeObserver(() => {
            this.updateDisplay();
        });
        setTimeout(() => {
            const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
            if (widget) this.widgetResizeObserver?.observe(widget);
        }, 0);
    }

    private fetchSymbols(): Array<{ value: string; label: string }> {
        // @ts-ignore
        return ((window as any).getSymbolOptions ? (window as any).getSymbolOptions() : getSymbolOptions()).map((s: any) => ({ value: s.symbol, label: s.symbol }));
    }

    private getDefaultConfig(): Partial<ExchangeCardsWidgetConfig> {
        const symbols = this.fetchSymbols();
        const today = new Date();
        const endDate = today.toISOString().slice(0, 10);
        const startDateObj = new Date(today);
        startDateObj.setMonth(today.getMonth() - 1);
        if (startDateObj.getMonth() === today.getMonth()) {
            startDateObj.setDate(0);
        }
        const startDate = startDateObj.toISOString().slice(0, 10);
        return {
            headerPinned: true,
            symbol: symbols[0].value,
            theme: 'default-dark',
            width: '100%',
            height: '400px',
            autosize: true,
            p_min_yield: 4,
            p_top_n: 10,
            p_sort_by: 'yield',
            p_exclude_special_dividends: true,
            start_date: startDate,
            end_date: endDate,
            isSymbolSelectable: true,
            selectedExchange: 'NASDAQ'
        };
    }

    private resolveContainer(container: string | HTMLElement): HTMLElement {
        if (typeof container === 'string') {
            const el = document.getElementById(container) || document.querySelector(container);
            if (!el) throw new Error(`Container not found: ${container}`);
            return el as HTMLElement;
        }
        return container;
    }

    private renderExchangeFilterOptions(): string {
        const options = ['NASDAQ', 'NYSE', 'AMEX', 'NASDAQCM', 'OTC'];
        const selected = this.getSelectedExchange();
        return options.map((option) => `<option value="${option}"${option === selected ? ' selected' : ''}>${option}</option>`).join('');
    }

    private getSelectedExchange(): string {
        return this.config.selectedExchange || 'NASDAQ';
    }

    private async init(): Promise<void> {
        if (!this.config.symbol) this.config.symbol = this.fetchSymbols()[0].value;
        this.createWidget();
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        if (widget) widget.classList.add('loading');
        await this.attachStyles();
        this.currentData = null;
        this.updateDisplay();
        this.emit('ready', {});
        this.config.onReady?.();
        this.setupChart();
        cancelSelectEvent(this.shadowRoot);
    }

    private createWidget(): void {
        let themeClass = 'defaultDarkMode';
      let dataTheme = 'default-dark';
      
      if (this.config.theme === 'white') {
        themeClass = 'whiteMode';
        dataTheme = 'white';
      } else if (this.config.theme === 'black') {
        themeClass = 'blackMode';
        dataTheme = 'black';
      } else if (this.config.theme === 'default-lite') {
  themeClass = 'defaultLightMode';
  dataTheme = 'default-lite';
}
        const widgetHtml = `
            <div style="width: ${typeof this.config.width == "number" ? this.config.width+ 'px' : this.config.width  };height: ${typeof this.config.height == "number" ? this.config.height+ 'px' : this.config.height};" class="widgetBox ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-theme="${dataTheme}">
                <div class="headTop">
                    <h4 class="title market">${this.getTitleIconSvg()}
                        Exchange Cards
                    </h4>
                    <div class="actionCol">${getHeaderPinButtonHTML(this.config)}</div>
                </div>
                <div class="inBody">
                    <div class="optionsRow">
                        ${this.config.hideSymbol ? '<div class="item dropStyle02"></div>' : `<div class="item dropStyle02">
                            <span id="symbol-dropdown-placeholder"></span>
                            <div id="selected-symbols" class="symbolsCol"></div>
                        </div>`}
                        <span></span>
                        <div class="item dateRange">
                            <select class="exchange-filter-select">${this.renderExchangeFilterOptions()}</select>
                        </div>
                        <div class="item dateRange">
                            <input type="text" class="date-input" />
                            <i><svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M9.91667 1.16667H8.75V0.583333C8.75 0.428624 8.68854 0.280251 8.57915 0.170854C8.46975 0.0614581 8.32138 0 8.16667 0C8.01196 0 7.86358 0.0614581 7.75419 0.170854C7.64479 0.280251 7.58333 0.428624 7.58333 0.583333V1.16667H4.08333V0.583333C4.08333 0.428624 4.02188 0.280251 3.91248 0.170854C3.80308 0.0614581 3.65471 0 3.5 0C3.34529 0 3.19692 0.0614581 3.08752 0.170854C2.97813 0.280251 2.91667 0.428624 2.91667 0.583333V1.16667H1.75C1.28587 1.16667 0.840752 1.35104 0.512563 1.67923C0.184375 2.00742 0 2.45254 0 2.91667V9.91667C0 10.3808 0.184375 10.8259 0.512563 11.1541C0.840752 11.4823 1.28587 11.6667 1.75 11.6667H9.91667C10.3808 11.6667 10.8259 11.4823 11.1541 11.1541C11.4823 10.8259 11.6667 10.3808 11.6667 9.91667V2.91667C11.6667 2.45254 11.4823 2.00742 11.1541 1.67923C10.8259 1.35104 10.3808 1.16667 9.91667 1.16667ZM10.5 9.91667C10.5 10.0714 10.4385 10.2198 10.3291 10.3291C10.2198 10.4385 10.0714 10.5 9.91667 10.5H1.75C1.59529 10.5 1.44692 10.4385 1.33752 10.3291C1.22812 10.2198 1.16667 10.0714 1.16667 9.91667V5.83333H10.5V9.91667ZM10.5 4.66667H1.16667V2.91667C1.16667 2.76196 1.22812 2.61358 1.33752 2.50419C1.44692 2.39479 1.59529 2.33333 1.75 2.33333H2.91667V2.91667C2.91667 3.07138 2.97813 3.21975 3.08752 3.32915C3.19692 3.43854 3.34529 3.5 3.5 3.5C3.65471 3.5 3.80308 3.43854 3.91248 3.32915C4.02188 3.21975 4.08333 3.07138 4.08333 2.91667V2.33333H7.58333V2.91667C7.58333 3.07138 7.64479 3.21975 7.75419 3.32915C7.86358 3.43854 8.01196 3.5 8.16667 3.5C8.32138 3.5 8.46975 3.43854 8.57915 3.32915C8.68854 3.21975 8.75 3.07138 8.75 2.91667V2.33333H9.91667C10.0714 2.33333 10.2198 2.39479 10.3291 2.50419C10.4385 2.61358 10.5 2.76196 10.5 2.91667V4.66667Z" fill="#9F9FA2"/> </svg> </i>
                        </div>
                    </div>
                    <div class="chartDv" id="topIndexContainer"></div>
                </div>
            </div>
        `;
        this.shadowRoot.innerHTML = widgetHtml;
    setupHeaderPin(this.shadowRoot, this.config);

        this.renderSelectedSymbols();

        const symbolFilterPlaceholder = this.shadowRoot.querySelector('.symbol-filter-placeholder');
        if (this.config.isSymbolSelectable && !this.config.hideSymbol) {
            const placeholder = this.shadowRoot.getElementById('symbol-dropdown-placeholder');
            if (placeholder) {
                const dropdownContainerOutter = document.createElement('div');
                dropdownContainerOutter.className = 'itemInner';
                const dropdownContainer = document.createElement('div');
                dropdownContainer.className = 'symbol-dropdown-container';

                const input = document.createElement('input');
                input.type = 'text';
                input.className = 'symbol-search-input';
                input.placeholder = 'Search symbol...';
                // input.style.cssText = 'width: 100%; box-sizing: border-box; padding: 8px 12px; border-radius: 6px; border: 1px solid #d1d5db; font-size: 15px; margin-bottom: 4px;';
                const dropdownList = document.createElement('div');
                dropdownList.className = 'symbol-dropdown-list-multiple';
                // dropdownList.style.maxHeight = '220px';
                // dropdownList.style.overflowY = 'auto';
                // dropdownList.style.background = '#000000';
                // dropdownList.style.position = 'absolute';
                // dropdownList.style.width = '100%';
                // dropdownList.style.zIndex = '1000';
                // dropdownList.style.display = 'none';

                const getAllOptions = () => ((window as any).getSymbolOptions ? (window as any).getSymbolOptions() : getSymbolOptions());

                let timeout = null as any;
                let requestId = 0;
                const renderDropdown = (filter = '') => {
                    clearTimeout(timeout);
                    timeout = setTimeout(async () => {
                    const id = ++requestId;
                    dropdownList.innerHTML = '<div style="padding:8px 12px;color:#888;">Searching...</div>';
                    const keyword = filter.trim();
                    let options = await searchSymbols({ keyword, market: 'stock' });
                    if (id !== requestId) return;
                    if (!keyword) {
                        const selectedMissing = this.selectedSymbols
                            .filter((s) => !options.some((item: any) => item.symbol === s))
                            .map((s) => getAllOptions().find((item: any) => item.symbol === s) || { symbol: s, name: s, type: 'stock' });
                        options = [...selectedMissing, ...options];
                    }
                    const selected = options.filter((opt: any) => this.selectedSymbols.includes(opt.symbol));
                    const unselected = options.filter((opt: any) => !this.selectedSymbols.includes(opt.symbol));
                    const sorted = [...selected, ...unselected];
                    dropdownList.innerHTML = '';
                    if (sorted.length === 0) {
                        dropdownList.innerHTML = '<div style="padding:8px 12px;color:#888;">No results</div>';
                        return;
                    }
                    sorted.forEach(opt => {
                        const divOutter = document.createElement('div');
                        divOutter.className = 'ditem';
                        const div = document.createElement('div');
                        div.className = 'trader-sm-card';
                        if (this.selectedSymbols.includes(opt.symbol)) {
                            divOutter.classList.add('selected-symbol-item');
                        }
                        div.innerHTML = getTraderSMCardHTML(opt);
                        divOutter.innerHTML = '<div class="checkIcon"><i><svg width="7" height="5" viewBox="0 0 7 5" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M0.5 2.5L2.42929 4.42929C2.46834 4.46834 2.53166 4.46834 2.57071 4.42929L6.5 0.5" stroke="white" stroke-linecap="round"/> </svg> </i></div><div class="trader-sm-card">' + div.getHTML() + '</div>';
                        divOutter.onclick = (e) => {
                            e.stopPropagation();
                            if (this.selectedSymbols.includes(opt.symbol)) {
                                this.selectedSymbols = this.selectedSymbols.filter(s => s !== opt.symbol);
                            } else {
                                this.selectedSymbols.push(opt.symbol);
                            }
                            renderDropdown(input.value);
                            this.renderSelectedSymbols();
                            this.setupChart();
                        };
                        dropdownList.appendChild(divOutter);
                    });
                    }, 300);
                };

                input.addEventListener('focus', () => {
                    dropdownList.style.display = 'block';
                    renderDropdown(input.value);
                });
                input.addEventListener('input', () => {
                    dropdownList.style.display = 'block';
                    renderDropdown(input.value);
                });
                input.addEventListener('blur', () => {
                    setTimeout(() => dropdownList.style.display = 'none', 150);
                });

                // dropdownContainer.style.position = 'relative';
                dropdownContainer.appendChild(input);
                dropdownContainer.appendChild(dropdownList);
                dropdownContainerOutter.appendChild(dropdownContainer);
                placeholder.replaceWith(dropdownContainerOutter);

                // Initial render
                renderDropdown();
            }
        }

        const input = this.shadowRoot.querySelector('.date-input') as HTMLInputElement;
        let defaultDates: string[] | undefined = undefined;
        if (this.config.start_date && this.config.end_date) {
            defaultDates = [this.config.start_date, this.config.end_date];
            input.value = `${this.config.start_date} to ${this.config.end_date}`;
        } else {
            const today = new Date();
            const endDate = today.toISOString().slice(0, 10);
            const startDateObj = new Date(today);
            startDateObj.setMonth(today.getMonth() - 1);
            if (startDateObj.getMonth() === today.getMonth()) {
                startDateObj.setDate(0);
            }
            const startDate = startDateObj.toISOString().slice(0, 10);
            defaultDates = [startDate, endDate];
            input.value = `${startDate} to ${endDate}`;
            this.config.start_date = startDate;
            this.config.end_date = endDate;
        }
        initFlatpickrInShadow(input, this.shadowRoot, {
            mode: 'range',
            dateFormat: 'Y-m-d',
            defaultDate: defaultDates,
            onClose: (selectedDates: Date[]) => {
                if (selectedDates.length === 2) {
                    const [start, end] = selectedDates;
                    this.config.start_date = start.toISOString().slice(0, 10);
                    this.config.end_date = end.toISOString().slice(0, 10);
                    input.value = `${this.config.start_date} to ${this.config.end_date}`;
                    this.setupChart();
                }
            },
        });

        const exchangeSelect = this.shadowRoot.querySelector('.exchange-filter-select') as HTMLSelectElement | null;
        if (exchangeSelect) {
            exchangeSelect.value = this.getSelectedExchange();
            exchangeSelect.addEventListener('change', () => {
                this.config.selectedExchange = exchangeSelect.value;
                this.setupChart();
            });
        }

        const filtersBox = this.shadowRoot.querySelector('.filtersBox') as HTMLElement | null;
        const filtersBtn = this.shadowRoot.querySelector('.optionsRow button') as HTMLButtonElement | null;
        if (filtersBtn && filtersBox) {
            filtersBtn.addEventListener('click', (ev: MouseEvent) => {
                ev.stopPropagation();
                filtersBox.classList.toggle('hidden');
            });

            filtersBox.addEventListener('click', (ev: MouseEvent) => ev.stopPropagation());
            filtersBox.addEventListener('pointerdown', (ev: PointerEvent) => ev.stopPropagation());

            this.outsideClickHandler = (ev: MouseEvent) => {
                try {
                    const path = (ev as any).composedPath ? (ev as any).composedPath() : (ev as any).path || [];
                    const clickedInside = Array.isArray(path) && path.some((node: any) => {
                        try {
                            return node && node.getRootNode && node.getRootNode() === this.shadowRoot;
                        } catch (e) {
                            return false;
                        }
                    });
                    if (!clickedInside) {
                        filtersBox.classList.add('hidden');
                    }
                } catch (e) {
                    filtersBox.classList.add('hidden');
                }
            };
            document.addEventListener('click', this.outsideClickHandler);
        }
    }

    onSelectChange(stock: any) {
        this.changeSymbol(stock.symbol, stock);
    }

    private async attachStyles(): Promise<void> {
        try {
            appendFlatpickrBaseStyles(this.shadowRoot);
            const styleEl = await getCSSStyles();
            this.shadowRoot.appendChild(styleEl);
        } catch (err) {
            console.error('Failed to load CSS file:', err);
        }
    }


    private async setupChart(): Promise<void> {
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        if (widget) widget.classList.add('loading');
        try {
            const params: Record<string, string> = {};
            const cfg = this.config as ExchangeCardsWidgetConfig;
            if (cfg.selectedExchange) params.selectedExchange = cfg.selectedExchange;
            if (cfg.start_date) params.start_date = cfg.start_date;
            if (cfg.end_date) params.end_date = cfg.end_date;
            if (this.selectedSymbols.length > 0) {
                params.symbols = this.selectedSymbols.join(',');
            } else if (cfg.symbol) {
                params.symbols = cfg.symbol;
            }
            const qs = Object.entries(params)
                .filter(([, value]) => value !== undefined && value !== null && value !== '')
                .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
                .join('&');
            const url = `${APIURL}/v1/exchange/cards${qs ? `?${qs}` : ''}`;

            const response = await apiGet({
                method: 'GET',
                url,
                headers: { 'Content-Type': 'application/json' }
            });

            if (!response.ok) throw new Error('Failed to fetch exchange cards data');
            const data = await response.json();
            this.currentData = this.normalizeExchangeCardsResponse(data);
            this.updateDisplay();
            this.emit('dataUpdate', { selectedExchange: this.getSelectedExchange(), data: this.currentData });
        } catch (err) {
            console.error('Error fetching exchange card data:', err);
            this.currentData = [];
            this.updateDisplay();
        } finally {
            if (widget) widget.classList.remove('loading');
        }
    }

    private normalizeExchangeCardsResponse(raw: any): any[] {
        const buildCardEntries = (source: Record<string, any> | null | undefined): any[] => {
            if (!source || typeof source !== 'object' || Array.isArray(source)) return [];
            return Object.entries(source).map(([key, value]: [string, any]) => {
                const isObject = value && typeof value === 'object' && !Array.isArray(value);
                const normalized: any = {
                    key,
                    label: isObject && value?.label ? value.label : key.replace(/_/g, ' ').replace(/\b\w/g, (char: string) => char.toUpperCase()),
                    value: isObject ? (value?.value ?? value?.display ?? '') : value,
                    display: isObject ? (value?.display ?? this.getDisplayText(value, ['value', 'display'])) : this.getDisplayText({ value }, ['value', 'display']),
                    detail: isObject ? (value?.sector_name || (value?.percentage != null ? `${value.percentage}%` : '')) : ''
                };

                if (isObject) {
                    Object.assign(normalized, value);
                }

                return normalized;
            });
        };

        if (raw?.cards && typeof raw.cards === 'object' && !Array.isArray(raw.cards)) {
            return buildCardEntries(raw.cards);
        }

        if (raw?.raw_data && typeof raw.raw_data === 'object' && !Array.isArray(raw.raw_data)) {
            return buildCardEntries(raw.raw_data);
        }

        if (raw?.data && typeof raw.data === 'object' && !Array.isArray(raw.data)) {
            if (raw.data.cards && typeof raw.data.cards === 'object' && !Array.isArray(raw.data.cards)) {
                return buildCardEntries(raw.data.cards);
            }
            if (raw.data.raw_data && typeof raw.data.raw_data === 'object' && !Array.isArray(raw.data.raw_data)) {
                return buildCardEntries(raw.data.raw_data);
            }
        }

        if (Array.isArray(raw)) return raw;
        if (raw && typeof raw === 'object') {
            const nested = raw.data ?? raw.items ?? raw.results ?? raw.cards ?? raw.exchange_cards ?? raw.exchangeCards;
            if (Array.isArray(nested)) return nested;
            if (nested && typeof nested === 'object') {
                const values = Object.values(nested as Record<string, unknown>);
                if (values.length && values.every((value) => typeof value === 'object' && value !== null)) {
                    return values as any[];
                }
            }
            if (raw.symbol || raw.name || raw.exchange || raw.title) {
                return [raw];
            }
        }
        return [];
    }

    private getDisplayText(item: any, keys: string[]): string {
        for (const key of keys) {
            const value = item?.[key];
            if (value === null || value === undefined || value === '') continue;
            if (typeof value === 'string') return value;
            if (typeof value === 'number') return String(value);
            if (typeof value === 'boolean') return value ? 'Yes' : 'No';
            if (typeof value === 'object') {
                if (Array.isArray(value) && value.length > 0) return String(value[0]);
                if (value.name || value.title || value.symbol) return this.getDisplayText(value, ['name', 'title', 'symbol', 'label']);
            }
        }
        return '-';
    }

    private escapeHtml(value: string): string {
        return String(value)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');
    }

    private updateDisplay(): void {
        if (!this.currentData) {
            return;
        }
        this.renderAnalystRating();
    }

    private renderAnalystRating(): void {
        const container = this.shadowRoot.querySelector('#topIndexContainer');
        if (!container) return;

        const items = Array.isArray(this.currentData)
            ? this.currentData
            : (this.currentData && (this.currentData.data || this.currentData.items)) || [];
        if (Array.isArray(items) && items.length > 0) {
            container.setAttribute('data-testing-id', 'Price Target Consensus Dashboard');
        } else {
            container.removeAttribute('data-testing-id');
        }

        if (!Array.isArray(items) || items.length === 0) {
            container.innerHTML = getNoDataHTML();
            return;
        }

        const cards = items.map((item: any, index: number) => {
            const title = this.escapeHtml(this.getDisplayText(item, ['label', 'name', 'title', 'company_name', 'companyName', 'symbol', 'ticker', 'exchange', 'exchange_name', 'sector_name']));
            const primaryValue = this.escapeHtml(this.getDisplayText(item, ['display', 'value', 'price', 'last_price', 'current_price', 'market_price', 'close', 'price_value']));
            const detailValue = this.escapeHtml(this.getDisplayText(item, ['detail', 'sector_name', 'industry', 'country', 'status', 'type']));

            return `
                <div class="item" data-testing-id="market-exchange-cards-item-${index}" data-index="${index}">
                    <div class="subRow rowitm">
                        <div class="colItm">
                            <p>
                                ${title}
                                <strong class="red">${primaryValue}</strong>
                            </p>
                        </div>
                    </div>
                    ${detailValue !== '-' ? `<div class="subRow rowitm"><div class="colItm"><p><strong>${detailValue}</strong></p></div></div>` : ''}
                </div>
            `;
        }).join('');

        container.innerHTML = `<div class="ptcDash marketExchangeCardsMain"><div class="inner">${cards}</div></div>`;
    }

    // Remove symbol from selectedSymbols and update UI
    private removeSymbol(symbol: string): void {
        this.selectedSymbols = this.selectedSymbols.filter(s => s !== symbol);
        this.renderSelectedSymbols();
        this.setupChart();
    }

    private renderSelectedSymbols(): void {
        const container = this.shadowRoot.getElementById('selected-symbols');
        if (!container) return;
        const symbolOptions = ((window as any).getSymbolOptions ? (window as any).getSymbolOptions() : getSymbolOptions());
        container.innerHTML = this.selectedSymbols.map(symbol => {
            const symInfo = symbolOptions.find((s: any) => s.symbol === symbol) || { symbol };
            let logoImg = '';
            if (symInfo.type === 'crypto') {
                logoImg = `<img src="https://media.traderverse.io/assets/crypto/${symbol.toUpperCase()}_CRYPTO.svg" alt="${symbol}" onerror="this.onerror=null;this.src='https://cdn-images.traderverse.io/crypto_dummy.svg';">`;
            } else {
                logoImg = `<img src="https://imagedelivery.net/09I_Y_JIwzvjUiGrV3dv3Q/stocks_v1/${symbol}.svg/public" alt="${symbol}">`;
            }
            return `<figure> ${logoImg} </figure>`;
        }).join('') + '<svg width="11" height="6" viewBox="0 0 11 6" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M0.706223 0.706055L5.41424 4.47247L10.1223 0.706055" stroke="#9A95AC" stroke-width="1.41241" stroke-linecap="round" stroke-linejoin="round"/> </svg>';
        // Add remove event
        container.querySelectorAll('.remove-selected-symbol').forEach(el => {
            el.addEventListener('click', (e: any) => {
                const symbol = e.target.getAttribute('data-symbol');
                this.selectedSymbols = this.selectedSymbols.filter(s => s !== symbol);
                this.renderSelectedSymbols();
                this.setupChart();
            });
        });
    }

    private changeSymbol(stockId: string, stock: any = null): void {
        if (!this.selectedSymbols.includes(stockId)) {
            this.selectedSymbols.push(stockId);
            this.renderSelectedSymbols();
            this.setupChart();
        }
        changeSymbolForWidget(this.shadowRoot, this.config, stock, stockId, (stockId: any) => this.setupChart(), (type: any, data: any) => this.emit(type, data));
    }

    public on(type: WidgetEventType, handler: WidgetEventHandler): void {
        if (!this.eventHandlers.has(type)) this.eventHandlers.set(type, []);
        this.eventHandlers.get(type)!.push(handler);
    }
    public off(type: WidgetEventType, handler: WidgetEventHandler): void {
        const list = this.eventHandlers.get(type);
        if (list) this.eventHandlers.set(type, list.filter(h => h !== handler));
    }
    public setSymbol(symbol: string): void {
        this.changeSymbol(symbol);
        const dropdown = this.shadowRoot.querySelector('.symbol-dropdown') as HTMLSelectElement;
        if (dropdown) dropdown.value = symbol;
    }
    public async getWidgetImage(): Promise<string> {
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        return await getWidgetDataURI(widget);
    }
  
    public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite'): void {
      if (!this.shadowRoot) return;
    
      this.config.theme = toggleWidgetTheme(
        this.shadowRoot,
        theme
      ) as 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite';
    
      this.updateDisplay();
    }
    public destroy(): void {
        if (this.updateInterval) { clearInterval(this.updateInterval); this.updateInterval = null; }
        if (this.widgetResizeObserver) { this.widgetResizeObserver.disconnect(); this.widgetResizeObserver = null; }
        if (this.outsideClickHandler) {
            document.removeEventListener('click', this.outsideClickHandler);
            this.outsideClickHandler = null;
        }
        this.eventHandlers.clear(); this.shadowRoot.innerHTML = '';
    }
    private emit(type: WidgetEventType, data: any): void {
        const handlers = this.eventHandlers.get(type);
        if (handlers) { const evt: WidgetEvent = { type, data }; handlers.forEach(h => h(evt)); }
    }
    private getTitleIconSvg(): string {
      return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M18.75 28.4375H11.25C4.4625 28.4375 1.5625 25.5375 1.5625 18.75V11.25C1.5625 4.4625 4.4625 1.5625 11.25 1.5625H18.75C25.5375 1.5625 28.4375 4.4625 28.4375 11.25V18.75C28.4375 25.5375 25.5375 28.4375 18.75 28.4375ZM11.25 3.4375C5.4875 3.4375 3.4375 5.4875 3.4375 11.25V18.75C3.4375 24.5125 5.4875 26.5625 11.25 26.5625H18.75C24.5125 26.5625 26.5625 24.5125 26.5625 18.75V11.25C26.5625 5.4875 24.5125 3.4375 18.75 3.4375H11.25Z" fill="currentColor"/> <path d="M9.16252 19.05C8.96252 19.05 8.76252 18.9875 8.58752 18.85C8.17502 18.5375 8.10002 17.95 8.41252 17.5375L11.3875 13.675C11.75 13.2125 12.2625 12.9125 12.85 12.8375C13.425 12.7625 14.0125 12.925 14.475 13.2875L16.7625 15.0875C16.85 15.1625 16.9375 15.1625 17 15.15C17.05 15.15 17.1375 15.125 17.2125 15.025L20.1 11.3C20.4125 10.8875 21.0125 10.8125 21.4125 11.1375C21.825 11.45 21.9 12.0375 21.575 12.45L18.6875 16.175C18.325 16.6375 17.8125 16.9375 17.225 17C16.6375 17.075 16.0625 16.9125 15.6 16.55L13.3125 14.75C13.225 14.675 13.125 14.675 13.075 14.6875C13.025 14.6875 12.9375 14.7125 12.8625 14.8125L9.88752 18.675C9.72502 18.925 9.45002 19.05 9.16252 19.05Z" fill="currentColor"/> </svg>`;
    }

}

declare global { interface Window { TraderverseMarketExchangeCards: typeof TraderverseMarketExchangeCards; } }
if (typeof window !== 'undefined') { window.TraderverseMarketExchangeCards = TraderverseMarketExchangeCards; }
